/**
 * Gregorian <-> Julian Day Number plumbing, plus the Solar Hijri (Jalali /
 * Shamsi) calendar.
 *
 * The Solar Hijri year starts at the true vernal equinox, so its leap pattern
 * is not periodic and cannot be captured by a closed-form rule. We therefore
 * read it from the runtime's ICU `persian` calendar via `icu.ts`, and keep
 * only the Gregorian half (which *is* arithmetic) here.
 */

import { icuToJdn, icuJdnTo, supportsCalendar } from "./icu.js";

const div = (a: number, b: number): number => Math.trunc(a / b);

/** Solar Hijri years the UI will display. */
export const JALALI_MIN_YEAR = 1178; // 1799 CE
export const JALALI_MAX_YEAR = 1633; // 2254 CE

// ---------------------------------------------------------------------------
// Gregorian <-> Julian Day Number (Fliegel-Van Flandern)
// ---------------------------------------------------------------------------

/**
 * This JDN convention numbers each day from its midnight, making it 0.5 below
 * the astronomical noon-based JDN. Only differences are compared here, and
 * the same offset is used consistently on both sides.
 */
export function gregorianToJdn(gy: number, gm: number, gd: number): number {
  const a = div(14 - gm, 12);
  const y = gy + 4800 - a;
  const m = gm + 12 * a - 3;
  return (
    gd + div(153 * m + 2, 5) + 365 * y + div(y, 4) - div(y, 100) + div(y, 400) - 32045
  );
}

export function jdnToGregorian(jdn: number): {
  gy: number;
  gm: number;
  gd: number;
} {
  const a = jdn + 32044;
  const b = div(4 * a + 3, 146097);
  const c = a - div(146097 * b, 4);
  const d = div(4 * c + 3, 1461);
  const e = c - div(1461 * d, 4);
  const m = div(5 * e + 2, 153);
  return {
    gd: e - div(153 * m + 2, 5) + 1,
    gm: m + 3 - 12 * div(m, 10),
    gy: 100 * b + d - 4800 + div(m, 10),
  };
}

export function isGregorianLeapYear(gy: number): boolean {
  return (gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0;
}

const GREGORIAN_MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export function gregorianMonthLength(gy: number, gm: number): number {
  if (gm === 2) return isGregorianLeapYear(gy) ? 29 : 28;
  return GREGORIAN_MONTH_LENGTHS[gm - 1] ?? 0;
}

export function isValidGregorian(gy: number, gm: number, gd: number): boolean {
  if (!Number.isInteger(gy) || !Number.isInteger(gm) || !Number.isInteger(gd)) {
    return false;
  }
  if (gm < 1 || gm > 12 || gy < 1) return false;
  return gd >= 1 && gd <= gregorianMonthLength(gy, gm);
}

// ---------------------------------------------------------------------------
// Solar Hijri, delegated to ICU
// ---------------------------------------------------------------------------

export type JalaliDate = { jy: number; jm: number; jd: number };

const PERSIAN_AVAILABLE = supportsCalendar("persian");

/** True when the host can compute the astronomical Persian calendar. */
export function isJalaliAstronomical(): boolean {
  return PERSIAN_AVAILABLE;
}

export function jalaliToJdn(jy: number, jm: number, jd: number): number {
  assertJalaliYear(jy);
  return icuToJdn({ year: jy, month: jm, day: jd }, "persian");
}

export function jdnToJalali(jdn: number): JalaliDate {
  const d = icuJdnTo(jdn, "persian");
  return { jy: d.year, jm: d.month, jd: d.day };
}

function assertJalaliYear(jy: number): void {
  if (jy < JALALI_MIN_YEAR || jy > JALALI_MAX_YEAR) {
    throw new RangeError(
      `Solar Hijri year ${jy} is outside the supported range ` +
        `(${JALALI_MIN_YEAR}-${JALALI_MAX_YEAR} SH)`,
    );
  }
}

/** True when the Solar Hijri year has 366 days. */
export function isJalaliLeapYear(jy: number): boolean {
  assertJalaliYear(jy);
  return (
    icuToJdn({ year: jy + 1, month: 1, day: 1 }, "persian") -
      icuToJdn({ year: jy, month: 1, day: 1 }, "persian") ===
    366
  );
}

/** Days in a Solar Hijri month: 31 for 1-6, 30 for 7-11, 29 or 30 for 12. */
export function jalaliMonthLength(jy: number, jm: number): number {
  if (jm < 1 || jm > 12) throw new RangeError("Invalid Jalali month: " + jm);
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  return isJalaliLeapYear(jy) ? 30 : 29;
}

export function isValidJalali(jy: number, jm: number, jd: number): boolean {
  if (!Number.isInteger(jy) || !Number.isInteger(jm) || !Number.isInteger(jd)) {
    return false;
  }
  if (jy < JALALI_MIN_YEAR || jy > JALALI_MAX_YEAR) return false;
  if (jm < 1 || jm > 12) return false;
  return jd >= 1 && jd <= jalaliMonthLength(jy, jm);
}
